import {
  Checkbox,
  Dialog,
  DialogContent,
  Divider,
  useTheme,
} from "@mui/material";
import * as React from "react";
import { useHref } from "react-router";

import { WorkspaceRecord } from "@/shared/domain/workspaces/WorkspaceRecord";
import { SearchParams } from "@/shared/environment";
import { CloseIcon, LinkIcon } from "@/shared/foundation/icons/mui";
import { Goto } from "@/shared/navigation/Goto";
import { nanoSecToSecStr } from "@/shared/time";

import { RobotoIconButton } from "../RobotoIconButton";
import { RobotoTypography } from "../RobotoTypography";
import { useWorkspaceTimer } from "../visualization/WorkspaceCtx";

import { AccessibilityContent } from "./AccessibilityContent";
import { ShareableLinkField } from "./ShareableLinkField";
import styles from "./ShareWorkspaceModal.module.css";

interface ShareWorkspaceModalProps {
  open: boolean;
  record: WorkspaceRecord | null;
  handleClose: () => void;
  error: Error | null;
}

export const ShareWorkspaceModal: React.FC<ShareWorkspaceModalProps> = ({
  open,
  record,
  handleClose,
  error,
}) => {
  const theme = useTheme();
  const [includeTimecode, setIncludeTimecode] = React.useState(true);
  const link = useSharedWorkspaceLink(record, includeTimecode);
  const workspaceId = record?.workspace_id;

  return (
    <Dialog open={open} fullWidth={true} maxWidth="sm" onClose={handleClose}>
      <div className={styles.dialogHeader}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-xs">
            <LinkIcon
              className={styles.titleIcon}
              sx={{ color: theme.palette.text.primary }}
            />
            <h2 className={styles.titleText}>Share Workspace</h2>
          </div>
          <RobotoIconButton
            className={styles.closeButton}
            onClick={handleClose}
            eventName="ShareWorkspaceModalCloseClicked"
          >
            <CloseIcon className={styles.closeIcon} />
          </RobotoIconButton>
        </div>
        <p
          className={styles.subtitle}
          style={{ color: theme.palette.text.secondary }}
        >
          Share this workspace with your organization or make it accessible to
          others.
        </p>
      </div>
      <DialogContent className={styles.dialogContent}>
        {error && (
          <RobotoTypography
            className={styles.errorText}
            sx={{ color: theme.palette.error.main }}
          >
            Failed to create shared workspace. Try again.
          </RobotoTypography>
        )}
        {!error && (
          <>
            <p className={styles.linkLabel}>Workspace share link</p>
            <ShareableLinkField link={link} />
            <label className={styles.timecodeRow}>
              <Checkbox
                checked={includeTimecode}
                onChange={(e) => setIncludeTimecode(e.target.checked)}
                size="small"
                sx={{ padding: 0 }}
              />
              <span className={styles.timecodeLabel}>Include timecode</span>
            </label>
            <Divider sx={{ my: 2 }} />
            {workspaceId && <AccessibilityContent workspaceId={workspaceId} />}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

/**
 * Builds a URL for sharing the given workspace.
 *
 * Uses `Goto.createVisualizerRoute` + `useHref` to construct the path,
 * which correctly handles the router basename for dev stack deployments.
 */
function useSharedWorkspaceLink(
  workspaceRecord: WorkspaceRecord | null,
  includeTimecode: boolean,
) {
  const timer = useWorkspaceTimer();

  const workspaceId = workspaceRecord?.workspace_id;
  const routePath = Goto.createVisualizerRoute(workspaceId);
  const href = useHref(routePath);

  const url = new URL(href, window.location.origin);
  url.search = "";

  if (includeTimecode) {
    url.searchParams.set(
      SearchParams.TIMESTAMP,
      nanoSecToSecStr(timer.currentTime),
    );
  }

  return url.href;
}
